import type { ReportDefinition } from '@ofmos/shared/report';
import { BarChart, type ChartPoint, DonutChart, Funnel, LineChart, Tile } from '../../ui';

/**
 * ONE DEFINITION, ONE CHART (`docs/decisions/reports.md` T1, T7).
 *
 * The switch from a chart TYPE to a chart PRIMITIVE lives here and nowhere else, so the
 * list's preview, the report page and the editor's live preview cannot disagree about what
 * a "stacked bar" looks like. Every one of them renders this component at a different
 * size, which is the only difference between them - the primitives take their width from
 * their container and their height from a prop.
 *
 * IT DRAWS THE RUN RESULT AND RUNS NOTHING. The caller owns the query, because the three
 * callers want different freshness: a preview card is happy with a cached answer, a report
 * page refetches when the range moves, and the editor re-runs on every keystroke that
 * changes the definition.
 */

export interface ReportChartProps {
  readonly definition: ReportDefinition;
  readonly name: string;
  readonly points: readonly ChartPoint[];
  readonly series: readonly import('../../ui').ChartSeries[];
  readonly total: number | null;
  readonly height: number;
  readonly loading?: boolean | undefined;
  readonly refetching?: boolean | undefined;
  readonly emptyMessage?: string | undefined;
  readonly format?: ((value: number) => string) | undefined;
  /** Clicking a bar, a slice or a stage. Absent on a preview, where a click navigates. */
  readonly onSelect?: ((point: ChartPoint) => void) | undefined;
  /** Hides the chart's own caption where the page already carries the title. */
  readonly titleHidden?: boolean | undefined;
}

export function ReportChart(props: ReportChartProps) {
  const { definition, name, points, series, height, loading, refetching, format } = props;
  const common = {
    name,
    height,
    ...(props.titleHidden === undefined ? {} : { titleHidden: props.titleHidden }),
    ...(loading === undefined ? {} : { loading }),
    ...(refetching === undefined ? {} : { refetching }),
    ...(props.emptyMessage === undefined ? {} : { emptyMessage: props.emptyMessage }),
    ...(format === undefined ? {} : { format }),
  };

  switch (definition.chart) {
    case 'bar':
    case 'bar_stacked':
      return (
        <BarChart
          {...common}
          series={series}
          stacked={definition.chart === 'bar_stacked'}
          /**
           * HORIZONTAL FOR A CATEGORY, VERTICAL FOR TIME. A month runs left to right
           * because a calendar does; a list of packages does not, and giving its labels the
           * left gutter is the difference between reading them and reading four characters
           * of each.
           */
          layout={definition.groupBy?.granularity === undefined ? 'horizontal' : 'vertical'}
          {...(props.onSelect === undefined ? {} : { onSelect: props.onSelect })}
        />
      );
    case 'line':
    case 'area':
      return <LineChart {...common} series={series} area={definition.chart === 'area'} />;
    case 'donut':
      return (
        <DonutChart
          {...common}
          points={points}
          {...(props.onSelect === undefined ? {} : { onSelect: props.onSelect })}
        />
      );
    case 'funnel':
      return (
        <Funnel
          {...common}
          stages={points}
          {...(props.onSelect === undefined ? {} : { onSelect: props.onSelect })}
        />
      );
    case 'number':
      /**
       * The product's one tile (`ui/Tile`, which replaced the `StatTile` R3 names in BR-PARTS)
       * in its `card` look, which is StatTile's rendering kept as it was (DESIGN.md B3: this
       * page is outside the five redesigned ones) - not a new big-number component. It is a link like every other tile, and its
       * destination is the report itself, because a big number's drill-down is "show me this
       * report", not "show me a group".
       */
      return (
        <Tile
          mode="link"
          look="card"
          label={name}
          value={props.total}
          to="#"
          {...(refetching === undefined ? {} : { refetching })}
        />
      );
  }
}
